import React, { useState, useEffect, useRef } from "react";
import styles from "./CustomSelect.module.scss";

interface Option {
    label: string;
    value?: string | number;
    disabled?: boolean;
}

interface CustomSelectProps {
    label: string;
    options: Option[];
    defaultValue?: string | number;
    value?: string | number;
    onChange: (val: string) => void;
    disabled?: boolean;
    visibleOptionsCount?: number;
    className?: string;
    customClasses?: {
        wrapper?: string;
        dropdown?: string;
        option?: {
            base?: string;
            active?: string;
            disabled?: string;
        };
    };
}

const CustomSelect: React.FC<CustomSelectProps> = ({
                                                       label,
                                                       options,
                                                       defaultValue,
                                                       value,
                                                       onChange,
                                                       disabled = false,
                                                       visibleOptionsCount = 5,
                                                       className = "",
                                                       customClasses = {}
                                                   }) => {
    const normalize = (val: string | number | undefined): string =>
        typeof val === "undefined" ? "" : String(val);

    const [open, setOpen] = useState(false);
    const [firstOpened, setOpened] = useState(false);
    const [selectedOption, setSelectedOption] = useState<Option | undefined>(undefined);
    const selectedRef = useRef<HTMLDivElement | null>(null);

    const [typedQuery, setTypedQuery] = useState("");
    const searchTimeout = useRef<NodeJS.Timeout | null>(null);


    useEffect(() => {
        if (typeof value !== "undefined") {
            const opt = options.find(
                (o) => normalize(o.value || o.label) === normalize(value)
            );
            if (opt) {
                setSelectedOption({ label: opt.label, value: normalize(opt.value || opt.label) });
            }
        }
    }, [value, options]);
    useEffect(() => {
        if (!open) return;

        let lastKeyTime = Date.now();

        const handleKeyPress = (e: KeyboardEvent) => {
            const char = e.key.length === 1 ? e.key : "";
            if (!char) return;

            const now = Date.now();
            const timeSinceLast = now - lastKeyTime;
            lastKeyTime = now;

            const shouldReset = timeSinceLast > 500;
            const newQuery = (shouldReset ? "" : typedQuery) + char;
            setTypedQuery(newQuery.toLowerCase());

            const match = options.find(opt =>
                !opt.disabled && opt.label.toLowerCase().startsWith(newQuery.toLowerCase())
            );
            if (match) {
                const val = normalize(match.value || match.label);
                onChange(val);
                setSelectedOption({ label: match.label, value: val });
            }

            if (searchTimeout.current) clearTimeout(searchTimeout.current);
            searchTimeout.current = setTimeout(() => setTypedQuery(""), 700);
        };

        window.addEventListener("keydown", handleKeyPress);
        return () => {
            window.removeEventListener("keydown", handleKeyPress);
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
        };
    }, [open, typedQuery, options, onChange]);



    useEffect(() => {
        if (
            typeof value === "undefined" &&
            typeof defaultValue !== "undefined" &&
            options.length > 0
        ) {
            const opt = options.find(
                (o) => normalize(o.value || o.label) === normalize(defaultValue)
            );
            if (opt) {
                setSelectedOption({ label: opt.label, value: normalize(opt.value || opt.label) });
            }
        }
    }, [defaultValue, options]);

    useEffect(() => {
        if (firstOpened) return;
        if (open) setOpened(true);

        if (open && selectedRef.current) {
            const container = selectedRef.current.parentElement;
            const element = selectedRef.current;
            if (container) {
                const offsetTop = element.offsetTop;
                const height = container.clientHeight;
                container.scrollTop = offsetTop - height / 2 + element.clientHeight / 2;
            }
        }
    }, [open]);

    const handleClick = (opt: Option) => {
        if (opt.disabled) return;
        const val = normalize(opt.value || opt.label);
        onChange(val);
        setSelectedOption({ label: opt.label, value: val });
        setOpen(false);
    };

    const showDefault =
        defaultValue &&
        !options.some(
            (o) => normalize(o.value || o.label) === normalize(defaultValue)
        );

    return (
        <div className={`${styles.selectWrapper} ${className} ${customClasses.wrapper || ""}`}>
            <div
                className={`${styles.selectHeader} ${disabled ? styles.disabled : ""}`}
                onClick={() => !disabled && setOpen((prev) => !prev)}
                tabIndex={0}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
            >
                <span>
                    {selectedOption?.label || (showDefault ? normalize(defaultValue) : "")}
                </span>
                <span className={`${styles.arrow} ${open ? styles.rotated : ""}`}>▼</span>
            </div>

            <label className={`${styles.floatingLabel} ${styles.active}`}>
                {label}
            </label>

            {open && (
                <div
                    className={`${styles.selectDropdown} ${customClasses.dropdown || ""}`}
                    style={{ maxHeight: `${visibleOptionsCount * 32}px` }}
                >
                    {showDefault && (
                        <div className={`${styles.option} ${styles.disabledOption} ${customClasses.option?.disabled || ""}`}>
                            {normalize(defaultValue)}
                        </div>
                    )}
                    {options.map((opt) => {
                        const val = normalize(opt.value || opt.label);
                        const isSelected = normalize(selectedOption?.value) === val;

                        const optionClasses = [
                            styles.option,
                            customClasses.option?.base || "",
                            isSelected ? styles.selected : "",
                            isSelected ? customClasses.option?.active || "" : "",
                            opt.disabled ? styles.disabledOption : "",
                            opt.disabled ? customClasses.option?.disabled || "" : ""
                        ].join(" ").trim();

                        return (
                            <div
                                key={val}
                                ref={isSelected ? selectedRef : null}
                                className={optionClasses}
                                onClick={() => handleClick(opt)}
                            >
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CustomSelect;